import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { initSystemTheme } from "./lib/init-system-theme";
import "./styles/globals.css";
import { initSentry } from "./lib/sentry";

// Uncaught errors and rejections reach Sentry through its global handlers.
initSentry();

// No CSRF bootstrap needed: the portal's only pre-login state-changing calls
// hit /msp/domain-login* which are CSRF-exempt, and the portal_csrf_token
// cookie is issued by the backend on login (set_portal_auth_cookies). The
// dashboard's shared ensureCSRFToken() would set the wrong cookie name here.

// Apply OS color-scheme preference. Portal users get no toggle UI by
// design — a customer-facing portal should follow the OS rather than
// surface yet another setting. Listener stays alive for app lifetime.
initSystemTheme();

// Create query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create router with query client context
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

// Register router type
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render app
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);
